
/* ==========================================================================
   CPB CUSTOM PREVIEW CART
   File: cpb-custom-preview-cart.js

   PURPOSE
   --------------------------------------------------------------------------
   Replace standard CPB cart and cart-drawer product images with the
   persistent, CPB-hosted personalised preview.

   The preview is uploaded by:
     cpb-custom-preview-upload.js

   Shopify line-item properties:
     _configId     = CPB configuration ID
     _cpb-preview  = Hosted personalised preview URL

   MATCHING
   --------------------------------------------------------------------------
   Match the cart item's _configId with the product link's:
     ?configid=XXXXXXXX

   This supports:
   - Main cart
   - Cart drawer / quick cart
   - Dynamic cart updates
   - Browser Back / Forward navigation

   NO LEGACY STORAGE
   --------------------------------------------------------------------------
   No sessionStorage.
   No localStorage.
   No product-handle restrictions.
   No legacy preview migration.

   Existing hosted-image matching architecture is preserved.
   ========================================================================== */

(() => {
  'use strict';

  // =========================================================================
  // STATE
  // =========================================================================

  let updateTimer = null;
  let updateRunning = false;
  let updateQueued = false;

  // =========================================================================
  // CART PROPERTY HELPERS
  // =========================================================================

  function getConfigId(item) {
    return (
      item?.properties?._configId ||
      ''
    ).trim();
  }

  function getHostedPreview(item) {
    return (
      item?.properties?.['_cpb-preview'] ||
      ''
    ).trim();
  }

  // =========================================================================
  // FETCH SHOPIFY CART
  // =========================================================================

  async function getCart() {
    const response = await fetch(
      '/cart.js',
      {
        credentials: 'same-origin',
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart request failed: ${response.status}`
      );
    }

    return response.json();
  }

  // =========================================================================
  // CART IMAGE REPLACEMENT
  // =========================================================================

  function replaceCartImages(cart) {

    /*
     * Apply the hosted personalised preview to the image.
     *
     * Remove responsive image attributes so the browser cannot
     * select the original CPB image instead.
     */
    function applyReplacement(img, replacement) {

      if (!img || !replacement) {
        return;
      }

      if (
        img.getAttribute('src') === replacement &&
        !img.getAttribute('srcset')
      ) {
        return;
      }

      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');

      img.srcset = '';
      img.src = replacement;
    }

    /*
     * Match the rendered cart product link to the exact CPB
     * configuration ID.
     *
     * Cornerstone uses:
     * ?configid=XXXXXXXX
     *
     * This works for both the main cart and cart drawer.
     */
    function linkMatchesConfigId(link, configId) {

      if (!link || !configId) {
        return false;
      }

      try {
        const url = new URL(
          link.href,
          location.href
        );

        return (
          url.searchParams.get('configid') === configId
        );
      } catch (_) {
        return false;
      }
    }

    /*
     * Process only cart items containing both:
     *
     * _configId
     * _cpb-preview
     *
     * All other products are left unchanged.
     */
    for (const item of cart.items) {

      const configId = getConfigId(item);
      const hostedPreview = getHostedPreview(item);

      if (!hostedPreview || !configId) {
        continue;
      }

      const productLinks = [
        ...document.querySelectorAll('a[href]')
      ];

      for (const link of productLinks) {

        if (!linkMatchesConfigId(link, configId)) {
          continue;
        }

        const img = link.querySelector('img');

        if (!img) {
          continue;
        }

        applyReplacement(
          img,
          hostedPreview
        );
      }
    }
  }

  // =========================================================================
  // MAIN CART UPDATE
  // =========================================================================

  async function updateCartPreviews() {

    if (updateRunning) {
      updateQueued = true;
      return;
    }

    updateRunning = true;
    updateQueued = false;

    try {
      const cart = await getCart();

      replaceCartImages(cart);

    } catch (_) {

      /*
       * Cart image replacement must never interfere
       * with normal Shopify cart functionality.
       */

    } finally {

      updateRunning = false;

      /*
       * If the cart changed during an update,
       * schedule one additional refresh.
       */
      if (updateQueued) {
        updateQueued = false;
        scheduleUpdate(120);
      }
    }
  }

  // =========================================================================
  // SCHEDULE CART UPDATE
  // =========================================================================

  function scheduleUpdate(delay = 150) {

    clearTimeout(updateTimer);

    updateTimer = setTimeout(
      () => {
        updateCartPreviews();
      },
      delay
    );
  }

  // =========================================================================
  // INITIAL LOAD
  // =========================================================================

  /*
   * No /cart restriction.
   *
   * The script also needs to work when the cart drawer
   * appears on product pages or other store pages.
   */
  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleUpdate(0);
      },
      {
        once: true
      }
    );

  } else {

    scheduleUpdate(0);
  }

  // =========================================================================
  // BROWSER BACK / FORWARD
  // =========================================================================

  window.addEventListener(
    'pageshow',
    () => {
      scheduleUpdate(0);
    }
  );

  // =========================================================================
  // DYNAMIC CART / CART DRAWER UPDATES
  // =========================================================================

  /*
   * Shopify and the theme can rebuild cart elements.
   *
   * Watch for inserted or removed DOM nodes and
   * reapply the hosted personalised preview.
   */
  const observer = new MutationObserver(
    mutations => {

      let relevantChange = false;

      for (const mutation of mutations) {

        if (mutation.type !== 'childList') {
          continue;
        }

        if (
          mutation.addedNodes.length ||
          mutation.removedNodes.length
        ) {
          relevantChange = true;
          break;
        }
      }

      if (relevantChange) {
        scheduleUpdate(180);
      }
    }
  );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  // =========================================================================
  // COMMON CART EVENTS
  // =========================================================================

  [
    'cart:updated',
    'cart:refresh',
    'cart:change'
  ].forEach(eventName => {

    document.addEventListener(
      eventName,
      () => {
        scheduleUpdate(100);
      }
    );

  });

  // =========================================================================
  // MANUAL CONSOLE HELPER
  // =========================================================================

  window.refreshCpbCustomCartPreviews =
    updateCartPreviews;

})();
